import { ReactNode, Suspense, lazy, useContext, useEffect, useState } from 'react';
import { Navigate, Route, RouteProps, useLocation, useParams } from 'react-router';

import useTracker from '../../hooks/tracker';
import Layout from '../../layouts/default';
import WebviewLayout from '../../layouts/webview';
import UpgradeCommunityPage from '../../pages/upgrade-community';
import { featureFlags } from '../../utils/feature-flags';
import { AppContext } from '../context';

const AddCompanyAppointmentBookingPage = lazy(
  () => import('../../pages/add-company/appointment-booking'),
);
const AddCompanyDataPage = lazy(() => import('../../pages/add-company/company-data'));
const AddCompanyEmployeesPage = lazy(() => import('../../pages/add-company/employees'));
const AddCompanyPersonalDataPage = lazy(() => import('../../pages/add-company/personal-data'));
const FmdFormPage = lazy(() => import('../../pages/add-company/fmd-form'));
const AddCompanySitesPage = lazy(() => import('../../pages/add-company/sites'));
const AdminPage = lazy(() => import('../../pages/admin'));
const AddMembersPage = lazy(() => import('../../pages/admin/add-members'));
const AddSitesPage = lazy(() => import('../../pages/admin/add-sites'));
const AdminDataTab = lazy(() => import('../../pages/admin/data'));
const AdminEmployeesTab = lazy(() => import('../../pages/admin/employees'));
const AdminOffersTab = lazy(() => import('../../pages/admin/offers'));
const AdminSitesTab = lazy(() => import('../../pages/admin/sites'));
const AdminTeamsTab = lazy(() => import('../../pages/admin/teams'));
const ChallengesPage = lazy(() => import('../../pages/challenges'));
const ChallengeDetailsPage = lazy(() => import('../../pages/challenge-details'));
const ChallengeFormPage = lazy(() => import('../../pages/challenge-form'));
const ConnectPage = lazy<(props: { isSubscription?: boolean }) => React.JSX.Element>(
  () => import('../../pages/connect'),
);
const CovelotafPage = lazy(() => import('../../pages/covelotaf'));
const CyclingPotentialPage = lazy(() => import('../../pages/cycling-potential'));
const FMDPage = lazy(() => import('../../pages/fmd'));
const FMDCertificatesPage = lazy(() => import('../../pages/fmd-certificates'));
const FMDConfigPage = lazy(() => import('../../pages/fmd-config'));
const FMDEmployeePage = lazy(() => import('../../pages/fmd-employee'));
const ForgotPasswordPage = lazy<(props: { isSubscription?: boolean }) => React.JSX.Element>(
  () => import('../../pages/forgot-password'),
);
const EventOnboardingConfirmedPage = lazy(() => import('../../pages/event-onboarding/confirm'));
const BfgExplorationMapWebviewPage = lazy(() => import('../../pages/webviews/bfg-exploration-map'));
const EventOnboardingOfferPage = lazy(() => import('../../pages/event-onboarding/offer'));
const EventPage = lazy(() => import('../../pages/event/index'));
const GetStartedPage = lazy(() => import('../../pages/get-started'));
const HomePage = lazy(() => import('../../pages/home'));
const HoldingPage = lazy(() => import('../../pages/holding'));
const JoinCommunityPage = lazy(() => import('../../pages/join-community'));
const JourneyFormPage = lazy(() => import('../../pages/journey-form'));
const LeaderboardByTeamsPage = lazy(() => import('../../pages/leaderboard-by-teams'));
const IndividualLeaderboardPage = lazy(() => import('../../pages/leaderboard-individual'));
const IncitationPage = lazy(() => import('../../pages/incitation'));
const LeaderboardsPage = lazy(() => import('../../pages/leaderboards'));
const LoginPage = lazy<(props: { isSubscription?: boolean }) => React.JSX.Element>(
  () => import('../../pages/login'),
);
const MobilitySurveyPage = lazy<(props: { isSubscription?: boolean }) => React.JSX.Element>(
  () => import('../../pages/mobility-survey'),
);
const MobilitySurveyConfigPage = lazy<(props: { isSubscription?: boolean }) => React.JSX.Element>(
  () => import('../../pages/mobility-survey-config'),
);
const MobilitySurveyPreviewPage = lazy<
  (props: { backPath?: string; isSubscription?: boolean }) => React.JSX.Element
>(() => import('../../pages/mobility-survey-preview'));
const NewPostPage = lazy(() => import('../../pages/new-post'));
const NewsPage = lazy(() => import('../../pages/news'));
const NotLinkedPage = lazy(() => import('../../pages/not-linked'));
const OnboardingAddressesPage = lazy(() => import('../../pages/onboarding/addresses'));
const OnboardingWorkAddressPage = lazy(() => import('../../pages/onboarding/work-address'));
const OnboardingPersonalDataPage = lazy(() => import('../../pages/onboarding/personal-data'));
const OnboardingTeamsPage = lazy(() => import('../../pages/onboarding/teams'));
const OnboardingTransportModesPage = lazy(() => import('../../pages/onboarding/transport-modes'));
const PDFViewerPage = lazy(() => import('../../pages/pdf-viewer'));
const PostUpdatePage = lazy(() => import('../../pages/post-update'));
const ProfilePage = lazy(() => import('../../pages/profile'));
const RegisterPage = lazy<(props: { isSubscription?: boolean }) => React.JSX.Element>(
  () => import('../../pages/register'),
);
const SearchCompanyPage = lazy(() => import('../../pages/search-company'));
const StatsPage = lazy(() => import('../../pages/stats'));
const StatsBySubgroupsPage = lazy(() => import('../../pages/stats-by-subgroups'));
const SubscriptionPage = lazy(() => import('../../pages/subscription'));
const ToolsPage = lazy(() => import('../../pages/tools'));
const ToolsCompaniesListPage = lazy(() => import('../../pages/tools/companies-list'));
const ToolsEventCampaignsPage = lazy(() => import('../../pages/tools/event-campaigns'));
const ToolsGroupsListPage = lazy(() => import('../../pages/tools/groups-list'));
const ToolsTransferPage = lazy(() => import('../../pages/tools/transfer'));
const ToolsUsersListPage = lazy(() => import('../../pages/tools/users-list'));
const TryForFreePage = lazy(() => import('../../pages/try-for-free'));
const AddCompanyWebviewPage = lazy(() => import('../../pages/webviews/add-company'));
const AddCompanyWebviewDonePage = lazy(() => import('../../pages/webviews/add-company/done'));
const EmployeeCO2SavingWebviewPage = lazy(() => import('../../pages/webviews/employee-co2-saving'));
const FMDWebviewPage = lazy(() => import('../../pages/webviews/fmd'));
const FMDHomeWorkJourneysCheckWebviewPage = lazy(
  () => import('../../pages/webviews/fmd/home-work-journeys-check'),
);
const OnboardingWebviewPage = lazy(() => import('../../pages/webviews/onboarding'));
const OnboardingWebviewDonePage = lazy(() => import('../../pages/webviews/onboarding/done'));
const WelcomePage = lazy(() => import('../../pages/welcome'));

export type TPaths =
  | '/'
  | 'subscription'
  | 'subscription/login'
  | 'subscription/forgot-password'
  | 'subscription/register'
  | 'subscription/connect'
  | 'subscription/upgrade-community'
  | 'subscription/personal-data'
  | 'subscription/company-data'
  | 'subscription/fmd-data'
  | 'subscription/appointment-booking'
  | 'subscription/sites'
  | 'subscription/employees'
  | 'join/:code'
  | 'try'
  | 'welcome'
  | 'welcome/login'
  | 'welcome/forgot-password'
  | 'welcome/register'
  | 'welcome/search-company'
  | 'welcome/not-linked'
  | 'onboarding/personal-data'
  | 'onboarding/addresses'
  | 'onboarding/work-address'
  | 'onboarding/team'
  | 'onboarding/transport-modes'
  | 'event-onboarding/:eventId/confirm'
  | 'event-onboarding/:eventId/offer'
  | 'events/:eventId'
  | 'cycling-potential'
  | 'incitation'
  | 'covelotaf'
  | 'mobility-survey/:code'
  | 'webviews'
  | 'webviews/employee-co2-saving'
  | 'webviews/bfg-exploration-map'
  | 'pdf-viewer';

export const defaultPrivatePath: TPaths = '/';
export const defaultGuestPath: `/${TPaths}` = '/welcome/login';

const routes: Array<
  Omit<RouteProps, 'path' | 'index' | 'lazy'> & {
    isGuest?: boolean;
    isPrivate?: boolean;
    path?: TPaths | '*';
  }
> = [
  {
    path: 'subscription',
    element: (
      <RouteElement pageName="subscription_1_welcome">
        <SubscriptionPage />
      </RouteElement>
    ),
    isGuest: true,
  },
  {
    path: 'subscription/login',
    element: (
      <RouteElement pageName="subscription_2_login">
        <LoginPage isSubscription />
      </RouteElement>
    ),
    isGuest: true,
  },
  {
    path: 'subscription/forgot-password',
    element: (
      <RouteElement pageName="subscription_2_forgot_password">
        <ForgotPasswordPage isSubscription />
      </RouteElement>
    ),
    isGuest: true,
  },
  {
    path: 'subscription/register',
    element: (
      <RouteElement pageName="subscription_2_register">
        <RegisterPage isSubscription />
      </RouteElement>
    ),
    isGuest: true,
  },
  {
    path: 'subscription/connect',
    element: (
      <RouteElement pageName="subscription_2_connect">
        <ConnectPage isSubscription />
      </RouteElement>
    ),
  },
  {
    path: 'subscription/upgrade-community',
    element: (
      <RouteElement pageName="subscription_2_2_upgrade_community">
        <UpgradeCommunityPage />
      </RouteElement>
    ),
    isPrivate: true,
  },
  {
    path: 'subscription/personal-data',
    element: (
      <RouteElement pageName="subscription_3_personal_data">
        <AddCompanyPersonalDataPage />
      </RouteElement>
    ),
    isPrivate: true,
  },
  {
    path: 'subscription/company-data',
    element: (
      <RouteElement pageName="subscription_4_company_data">
        <AddCompanyDataPage />
      </RouteElement>
    ),
    isPrivate: true,
  },
  {
    path: 'subscription/fmd-data',
    element: (
      <RouteElement pageName="subscription_4_fmd_data">
        <FmdFormPage />
      </RouteElement>
    ),
    isPrivate: true,
  },
  {
    path: 'subscription/appointment-booking',
    element: (
      <RouteElement pageName="subscription_7_appointment_booking">
        <AddCompanyAppointmentBookingPage />
      </RouteElement>
    ),
    isPrivate: true,
  },
  {
    path: 'subscription/sites',
    element: (
      <RouteElement pageName="subscription_6_sites">
        <AddCompanySitesPage />
      </RouteElement>
    ),
    isPrivate: true,
  },
  {
    path: 'subscription/employees',
    element: (
      <RouteElement pageName="subscription_5_employees">
        <AddCompanyEmployeesPage />
      </RouteElement>
    ),
    isPrivate: true,
  },
  {
    path: 'join/:code',
    element: (
      <RouteElement pageName="join_community">
        <JoinCommunityPage />
      </RouteElement>
    ),
    isGuest: true,
  },
  {
    path: 'try',
    element: (
      <RouteElement pageName="try_for_free_welcome">
        <TryForFreePage />
      </RouteElement>
    ),
    isGuest: true,
  },
  {
    path: 'welcome',
    element: (
      <RouteElement pageName="employee_welcome">
        <WelcomePage />
      </RouteElement>
    ),
    isGuest: true,
  },
  {
    path: 'welcome/login',
    element: (
      <RouteElement pageName="employee_login">
        <LoginPage />
      </RouteElement>
    ),
    isGuest: true,
  },
  {
    path: 'welcome/forgot-password',
    element: (
      <RouteElement pageName="employee_forgot_password">
        <ForgotPasswordPage />
      </RouteElement>
    ),
    isGuest: true,
  },
  {
    path: 'welcome/register',
    element: (
      <RouteElement pageName="employee_register">
        <RegisterPage />
      </RouteElement>
    ),
    isGuest: true,
  },
  {
    path: 'welcome/search-company',
    element: (
      <RouteElement pageName="employee_search_company">
        <SearchCompanyPage />
      </RouteElement>
    ),
    isGuest: true,
  },
  {
    path: 'welcome/not-linked',
    element: (
      <RouteElement pageName="employee_not_linked">
        <NotLinkedPage />
      </RouteElement>
    ),
    isPrivate: true,
  },
  {
    path: 'onboarding/personal-data',
    element: (
      <RouteElement pageName="onboarding_1_personal_data">
        <OnboardingPersonalDataPage />
      </RouteElement>
    ),
    isPrivate: true,
  },
  {
    path: 'onboarding/addresses',
    element: (
      <RouteElement pageName="onboarding_2_address">
        <OnboardingAddressesPage />
      </RouteElement>
    ),
    isPrivate: true,
  },
  {
    path: 'onboarding/work-address',
    element: (
      <RouteElement pageName="onboarding_work_address">
        <OnboardingWorkAddressPage />
      </RouteElement>
    ),
    isPrivate: true,
  },
  {
    path: 'onboarding/team',
    element: (
      <RouteElement pageName="onboarding_2_team">
        <OnboardingTeamsPage />
      </RouteElement>
    ),
    isPrivate: true,
  },
  {
    path: 'onboarding/transport-modes',
    element: (
      <RouteElement pageName="onboarding_3_transport_modes">
        <OnboardingTransportModesPage />
      </RouteElement>
    ),
    isPrivate: true,
  },
  {
    path: 'cycling-potential',
    element: (
      <RouteElement pageName="cycling_potential">
        <CyclingPotentialPage />
      </RouteElement>
    ),
  },
  {
    path: 'mobility-survey/:code',
    element: (
      <RouteElement pageName="mobility_survey">
        <MobilitySurveyPage />
      </RouteElement>
    ),
  },
  {
    path: 'pdf-viewer',
    element: (
      <RouteElement pageName="pdf_viewer">
        <PDFViewerPage />
      </RouteElement>
    ),
  },
  {
    path: 'webviews/employee-co2-saving',
    element: (
      <RouteElement pageName="employee_co2_saving">
        <EmployeeCO2SavingWebviewPage />
      </RouteElement>
    ),
  },
  {
    path: 'webviews',
    element: <WebviewLayout />,
    children: (
      <>
        <Route
          element={
            <RouteElement pageName="add_company_webview_done">
              <AddCompanyWebviewDonePage />
            </RouteElement>
          }
          path="add-company/done"
        />
        <Route
          element={
            <RouteElement pageName="add_company_webview">
              <AddCompanyWebviewPage />
            </RouteElement>
          }
          path="add-company"
        />
        <Route
          element={
            <RouteElement pageName="fmd_webview">
              <FMDWebviewPage />
            </RouteElement>
          }
          path="bicycle-compensation"
        />
        <Route
          element={
            <RouteElement pageName="fmd_work_journeys_check_webview">
              <FMDHomeWorkJourneysCheckWebviewPage />
            </RouteElement>
          }
          path="bicycle-compensation/home-work-journeys-check"
        />
        <Route
          element={
            <RouteElement pageName="onboarding_webview_done">
              <OnboardingWebviewDonePage />
            </RouteElement>
          }
          path="onboarding/done"
        />
        <Route
          element={
            <RouteElement pageName="onboarding_webview">
              <OnboardingWebviewPage />
            </RouteElement>
          }
          path="onboarding"
        />
        <Route
          element={
            <RouteElement pageName="bfg_exploration_map_webview">
              <BfgExplorationMapWebviewPage />
            </RouteElement>
          }
          path="bfg-exploration-map"
        />
      </>
    ),
  },
  {
    path: 'event-onboarding/:eventId/offer',
    isPrivate: true,
    element: (
      <RouteElement pageName="event_onboarding_offer">
        <EventOnboardingOfferPage />
      </RouteElement>
    ),
  },
  {
    path: 'event-onboarding/:eventId/confirm',
    isPrivate: true,
    element: (
      <RouteElement pageName="event_onboarding_confirm">
        <EventOnboardingConfirmedPage />
      </RouteElement>
    ),
  },
  {
    path: '/',
    isPrivate: true,
    element: <Layout />,
    children: (
      <>
        <Route
          index
          element={
            <RouteElement pageName="home_page">
              <HomePage />
            </RouteElement>
          }
        />
        <Route
          element={
            <RouteElement pageName="get_started">
              <GetStartedPage />
            </RouteElement>
          }
          path="get-started"
        />
        <Route
          element={
            <RouteElement pageName="challenges_hub">
              <ChallengesPage />
            </RouteElement>
          }
          path="challenges"
        />
        <Route
          element={
            <RouteElement pageName="leaderboards_hub">
              <LeaderboardsPage />
            </RouteElement>
          }
          path="leaderboards"
        />
        <Route
          element={
            <RouteElement pageName="leaderboard_individual">
              <IndividualLeaderboardPage />
            </RouteElement>
          }
          path="leaderboards/cyclists"
        />
        <Route
          element={
            <RouteElement pageName="leaderboard_by_teams">
              <LeaderboardByTeamsPage subgroupType="companyTeam" />
            </RouteElement>
          }
          path="leaderboards/teams"
        />
        <Route
          element={
            <RouteElement pageName="leaderboard_by_sites">
              <LeaderboardByTeamsPage subgroupType="companySite" />
            </RouteElement>
          }
          path="leaderboards/sites"
        />
        <Route
          element={
            <RouteElement pageName="holding_overview">
              <HoldingPage />
            </RouteElement>
          }
          path="holding"
        />
        <Route
          element={
            <RouteElement pageName="statistics_hub">
              <StatsPage />
            </RouteElement>
          }
          path="stats"
        />
        <Route
          element={
            <RouteElement pageName="statistics_by_sites">
              <StatsBySubgroupsPage subgroupType="companySite" />
            </RouteElement>
          }
          path="stats/sites"
        />
        <Route
          element={
            <RouteElement pageName="statistics_by_teams">
              <StatsBySubgroupsPage subgroupType="companyTeam" />
            </RouteElement>
          }
          path="stats/teams"
        />
        <Route
          element={
            <RouteElement pageName="statistics_by_subsidiaries">
              <StatsBySubgroupsPage subgroupType="companySubsidiary" />
            </RouteElement>
          }
          path="stats/subsidiaries"
        />
        <Route
          element={
            <RouteElement pageName="statistics_by_regions">
              <StatsBySubgroupsPage subgroupType="companyRegion" />
            </RouteElement>
          }
          path="stats/regions"
        />
        <Route
          element={
            <RouteElement pageName="news_list">
              <NewsPage />
            </RouteElement>
          }
          path="news"
        />
        <Route
          element={
            <RouteElement pageName="news_creation">
              <NewPostPage />
            </RouteElement>
          }
          path="new-post"
        />
        <Route
          element={
            <RouteElement pageName="news_update">
              <PostUpdatePage />
            </RouteElement>
          }
          path="post/:id/update"
        />
        <Route
          element={
            <RouteElement pageName="challenge_detail">
              <ChallengeDetailsPage />
            </RouteElement>
          }
          path="challenges/:id"
        />
        <Route
          element={
            <RouteElement pageName="event_detail">
              <EventPageRoute />
            </RouteElement>
          }
          path="events/:eventId"
        />
        <Route
          element={
            <RouteElement pageName="challenge_update">
              <ChallengeFormPage />
            </RouteElement>
          }
          path="challenges/:id/update"
        />
        <Route
          element={
            <RouteElement pageName="challenge_creation">
              <ChallengeFormPage />
            </RouteElement>
          }
          path="new-challenge"
        />
        <Route
          element={
            <RouteElement pageName="indemnity_calendar">
              <FMDPage />
            </RouteElement>
          }
          path="bicycle-compensation"
        />
        <Route
          element={
            <RouteElement pageName="indemnity_employee_certificates">
              <FMDCertificatesPage />
            </RouteElement>
          }
          path="bicycle-compensation/certificates"
        />
        <Route
          element={
            <RouteElement pageName="indemnity_parameters">
              <FMDConfigPage />
            </RouteElement>
          }
          path="bicycle-compensation/config"
        />
        <Route
          element={
            <RouteElement pageName="indemnity_employee_detail">
              <FMDEmployeePage />
            </RouteElement>
          }
          path="bicycle-compensation/employees/:id"
        />
        <Route
          element={<Navigate replace to="/incitation?tab=accessibility" />}
          path="accessibility"
        />
        <Route
          element={
            <RouteElement pageName="profile">
              <ProfilePage />
            </RouteElement>
          }
          path="profile"
        />
        <Route
          element={
            <RouteElement pageName="home_work_journey_update">
              <JourneyFormPage />
            </RouteElement>
          }
          path="profile/journeys/:id"
        />
        <Route
          element={
            <RouteElement pageName="home_work_journey_creation">
              <JourneyFormPage />
            </RouteElement>
          }
          path="profile/new-journey"
        />
        <Route
          element={
            <RouteElement>
              <AddMembersPage />
            </RouteElement>
          }
          path="admin/add-members"
        />
        <Route
          element={
            <RouteElement>
              <AddSitesPage />
            </RouteElement>
          }
          path="admin/add-sites"
        />
        <Route
          element={
            <RouteElement>
              <AdminPage />
            </RouteElement>
          }
          path="admin"
        >
          <Route element={<AdminDataTab />} path="data" />
          <Route element={<AdminSitesTab />} path="sites" />
          <Route element={<AdminEmployeesTab />} path="employees" />
          <Route element={<AdminTeamsTab />} path="teams" />
          <Route element={<AdminOffersTab />} path="offers" />
        </Route>
        <Route
          element={
            <RouteElement pageName="mobility_survey_config">
              <MobilitySurveyConfigPage />
            </RouteElement>
          }
          path="mobility-survey/config"
        />
        <Route
          element={
            <RouteElement pageName="mobility_survey_preview">
              <MobilitySurveyPreviewPage />
            </RouteElement>
          }
          path="mobility-survey/preview"
        />
        <Route
          element={
            <RouteElement pageName="mobility_survey_preview">
              <MobilitySurveyPreviewPage backPath="/incitation" />
            </RouteElement>
          }
          path="incitation/preview"
        />
        <Route element={<Navigate replace to="/incitation" />} path="cycling-practice" />
        <Route
          element={
            <RouteElement pageName="incitation_hub">
              <IncitationPage pageKey="incitation" />
            </RouteElement>
          }
          path="incitation"
        />
        <Route
          element={
            featureFlags.covelotaf ? (
              <RouteElement pageName="covelotaf">
                <CovelotafPage />
              </RouteElement>
            ) : (
              <Navigate replace to="/" />
            )
          }
          path="covelotaf"
        />
        <Route element={<Navigate replace to="/tools/companies" />} path="new-contract" />
        <Route
          element={
            <RouteElement pageName="tools">
              <ToolsPage />
            </RouteElement>
          }
          path="tools"
        />
        <Route
          element={
            <RouteElement pageName="tools_companies_list">
              <ToolsCompaniesListPage />
            </RouteElement>
          }
          path="tools/companies"
        />
        <Route
          element={
            <RouteElement pageName="tools_groups_list">
              <ToolsGroupsListPage />
            </RouteElement>
          }
          path="tools/groups"
        />
        <Route
          element={
            <RouteElement pageName="tools_event_campaigns">
              <ToolsEventCampaignsPage />
            </RouteElement>
          }
          path="tools/event-campaigns"
        />
        <Route
          element={
            <RouteElement pageName="tools_transfer">
              <ToolsTransferPage />
            </RouteElement>
          }
          path="tools/transfer"
        />
        <Route
          element={
            <RouteElement pageName="tools_users_list">
              <ToolsUsersListPage />
            </RouteElement>
          }
          path="tools/users"
        />
      </>
    ),
  },
];

function RouteElement({
  pageName,
  children,
}: {
  children: ReactNode;
  pageName?: string;
}): React.JSX.Element {
  const [initialized, setInitialized] = useState(false);
  const {
    partner: { current: currentPartner },
    user: { current: currentUser, employee },
  } = useContext(AppContext);
  const { trackEvent } = useTracker();
  const { pathname, search } = useLocation();

  useEffect(() => {
    setInitialized(true);
  }, []);

  useEffect(() => {
    if (pageName)
      trackEvent('Page Visited', {
        ...Object.fromEntries(new URLSearchParams(search)),
        pathname,
        page_name: pageName,
      });
  }, [initialized, currentUser, employee, currentPartner, pageName, pathname, search]);

  return <Suspense fallback={<></>}>{children}</Suspense>;
}

function EventPageRoute(): React.JSX.Element {
  const { eventId } = useParams<{ eventId: string }>();

  return <EventPage key={eventId} />;
}

export default routes;
